/* eslint-disable no-undef */
import { Modal, Input, Upload, Divider, Button } from 'antd';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

export const PatientNotesModal = ({
  patientDetails,
  setAttachmentsModal,
  isLoading,
  schedulepage,
  notesDocument,
  setNotesDocument,
  maxAllowedNotesLength,
  updateNotesDocument,
  attachments,
  renderDocument,
  createFileDocument,
  validateFile,
  source,
  handleDeleteMultipleDocs,
}) => {
  const minViewportVisibility = 64;
  const { user } = useAuth();
  const closeAttachmentsModal = () => {
    setAttachmentsModal(false);
  };

  const cancelNotesModal = () => {
    analytics.track(
      `Clicked Cancel In ${
        source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
      } History Modal`,
      {
        schedulepage,
        email: user?.email,
      }
    );
    closeAttachmentsModal();
  };

  const patientDetailsJsx = patientDetails ? (
    <div className="flex items-center gap-2 text-sm text-gray-500 font-normal">
      <div className="flex flex-wrap gap-4">
        <span className="flex gap-1">
          <span className="font-medium">Patient name:</span>
          {[
            patientDetails.title,
            patientDetails.first_name,
            patientDetails.last_name,
          ]
            .filter(Boolean)
            .join(' ')}
        </span>
        <span className="flex gap-1">
          <span className="font-medium">Patient ID:</span>
          {patientDetails.patient_id}
        </span>
        <span className="flex gap-1">
          <span className="font-medium">Date of birth:</span>
          <span>{patientDetails.birth_date}</span>
        </span>
      </div>
    </div>
  ) : null;

  return (
    <Modal
      centered
      open
      onCancel={cancelNotesModal}
      title={
        <div className="flex flex-col">
          Add history
          {patientDetailsJsx}
        </div>
      }
      styles={{
        content: {
          height: `calc(100vh - ${minViewportVisibility}px)`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          height: '100%',
        },
      }}
      width={1200}
      footer={
        <div className="flex justify-end gap-2 pt-2">
          {attachments.length > 0 && (
            <Button
              type="delete"
              className="border border-[#d63232] bg-[#d63232] flex items-center h-[38px] rounded-[12px] px-4 py-2 text-white text-[14px]"
              onClick={() => handleDeleteMultipleDocs(attachments, source)}
            >
              Delete documents
            </Button>
          )}
          <button
            disabled={isLoading}
            className="ih-button-primary gap-2"
            onClick={() => {
              analytics.track(
                `Clicked Save In ${
                  source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                } History Modal`,
                {
                  schedulepage,
                  email: user?.email,
                }
              );
              closeAttachmentsModal();
            }}
          >
            Save history
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 h-full">
        <div className="w-full flex-1">
          <Input.TextArea
            placeholder="Paste Notes"
            autoFocus
            style={{ resize: 'none', height: '100%' }}
            defaultValue={notesDocument.content}
            onChange={(e) => {
              setNotesDocument((prev) => ({
                ...prev,
                content: e.target.value,
              }));
            }}
            onBlur={(e) => {
              analytics.track(
                `Updated Notes Text In ${
                  source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
                } History`,
                {
                  schedulepage,
                  email: user?.email,
                }
              );
              if (e.target.value?.length > maxAllowedNotesLength) {
                return false;
              }
              updateNotesDocument(e.target.value);
            }}
          />
          <div
            className={`text-sm mt-2 text-gray-500 flex flex-wrap gap-2 ${
              notesDocument.content?.length > maxAllowedNotesLength
                ? 'text-red-500 justify-between'
                : 'justify-end'
            }`}
          >
            {notesDocument.content?.length > maxAllowedNotesLength && (
              <span>
                You have exceeded the maximum allowed character limit. Changes
                will not be saved.
              </span>
            )}
            <span className="text-right">
              {notesDocument.content?.length || 0} / {maxAllowedNotesLength}{' '}
              {' characters'}
            </span>
          </div>
        </div>
        <Divider style={{ marginBottom: 0 }}>
          <p className="text-sm text-gray-500">
            {attachments.length || 'Add'} attachment(s)
          </p>
        </Divider>
        {attachments?.length > 0 && (
          <div className="flex gap-4 overflow-y-auto shrink-0">
            {attachments.map(renderDocument)}
          </div>
        )}
        <Upload.Dragger
          disabled={isLoading}
          fileList={[]}
          name="files"
          accept=".doc,.docx,.txt,.pdf"
          onChange={async ({ file }) => {
            if (await validateFile(file)) {
              createFileDocument(file);
            }
          }}
          onClick={() => {
            analytics.track(
              `Clicked Draggable Area In ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History Modal`,
              {
                schedulepage,
                email: user?.email,
              }
            );
          }}
          onDrop={() => {
            analytics.track(
              `Dropped File In ${
                source === 'vca_patient_history' ? 'VCA' : 'Pre Chart'
              } History Modal`,
              {
                schedulepage,
                email: user?.email,
              }
            );
          }}
        >
          <p className="ant-upload-drag-icon">
            {isLoading ? <LoadingOutlined /> : <InboxOutlined />}
          </p>
          <p className="ant-upload-text text-[#00d090]">
            Click or drag file to this area to upload
            <p className="text-xs text-gray-500">txt, doc, docx, pdf</p>
          </p>
        </Upload.Dragger>
      </div>
    </Modal>
  );
};
